import { useMemo } from "react";
import type { EChartsOption } from "echarts";
import { ageGroupStats, genderMedians, histogram } from "../data/analytics";
import { AGE_GROUPS, CHART_COLORS } from "../data/constants";
import { formatDuration, formatNumber, formatPercent } from "../data/formatters";
import type { NormalizedRunner } from "../types";
import { Chart, chartGridColor, chartTextStyle } from "../components/Chart";
import { MetricCard } from "../components/MetricCard";
import { Panel } from "../components/Panel";

export function DemographicsPage({ runners }: { runners: NormalizedRunner[] }) {
  const ageGroups = useMemo(() => ageGroupStats(runners), [runners]);
  const knownAges = runners.filter((runner) => runner.age != null);
  const ages = useMemo(() => histogram(knownAges.map((runner) => runner.age!), 12), [knownAges]);
  const medians = useMemo(() => genderMedians(runners), [runners]);
  const unknownAge = runners.length - knownAges.length;

  const ageHistogram: EChartsOption = useMemo(() => ({
    textStyle: chartTextStyle,
    tooltip: { trigger: "axis", formatter: (params: unknown) => {
      const item = (params as Array<{ dataIndex: number; value: number }>)[0];
      const bin = ages[item.dataIndex];
      return `${Math.floor(bin.start)}–${Math.ceil(bin.end)} 歲<br/>${item.value.toLocaleString()} 人`;
    } },
    grid: { left: 58, right: 20, top: 18, bottom: 48 },
    xAxis: { type: "category", data: ages.map((bin) => `${Math.floor(bin.start)}`), name: "年齡", axisLabel: chartTextStyle, axisLine: { lineStyle: { color: chartGridColor } } },
    yAxis: { type: "value", name: "人數", axisLabel: chartTextStyle, splitLine: { lineStyle: { color: chartGridColor } } },
    series: [{ type: "bar", data: ages.map((bin) => bin.count), itemStyle: { color: CHART_COLORS[0] } }],
  }), [ages]);

  const ageGroupOption: EChartsOption = useMemo(() => ({
    textStyle: chartTextStyle,
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    grid: { left: 66, right: 20, top: 18, bottom: 88 },
    xAxis: { type: "category", data: ageGroups.map((group) => group.label), axisLabel: { ...chartTextStyle, rotate: 38 }, axisLine: { lineStyle: { color: chartGridColor } } },
    yAxis: { type: "value", name: "人數", axisLabel: chartTextStyle, splitLine: { lineStyle: { color: chartGridColor } } },
    series: [{ type: "bar", data: ageGroups.map((group) => group.value), itemStyle: { color: CHART_COLORS[1] }, label: { show: true, position: "top", color: "#dfe8f5", fontSize: 14 } }],
  }), [ageGroups]);

  const stackData = useMemo(() => {
    const labels = ["男性", "女性", "其他", "未知／未公開"];
    return labels.map((gender) => ({
      gender,
      values: AGE_GROUPS.map((ageGroup) => runners.filter((runner) => runner.ageGroup === ageGroup && runner.genderLabel === gender).length),
    }));
  }, [runners]);

  const ageStack: EChartsOption = useMemo(() => ({
    textStyle: chartTextStyle,
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { top: 0, textStyle: chartTextStyle },
    grid: { left: 62, right: 20, top: 56, bottom: 85 },
    xAxis: { type: "category", data: ageGroups.map((group) => group.label), axisLabel: { ...chartTextStyle, rotate: 38 }, axisLine: { lineStyle: { color: chartGridColor } } },
    yAxis: { type: "value", name: "人數", axisLabel: chartTextStyle, splitLine: { lineStyle: { color: chartGridColor } } },
    series: stackData.map((row, index) => ({ name: row.gender, type: "bar", stack: "gender", data: row.values, itemStyle: { color: CHART_COLORS[index] } })),
  }), [ageGroups, stackData]);

  return (
    <div className="page-stack">
      <div className="metric-grid metric-grid--four">
        {/* 本頁共同母數 */}
        <MetricCard label="篩選後達標者" value={formatNumber(runners.length)} note="" tone="blue" />
        <MetricCard label="年齡已知" value={formatNumber(knownAges.length)} note={formatPercent(knownAges.length / Math.max(1, runners.length))} tone="mint" />
        <MetricCard label="年齡未知" value={formatNumber(unknownAge)} note={`${formatPercent(unknownAge / Math.max(1, runners.length))}，圖表保留此類別`} tone="yellow" />
        <MetricCard label="年齡層數" value={String(ageGroups.filter((group) => group.value > 0).length)} note="含未知類別" tone="pink" />
      </div>

      <div className="content-grid">
        <Panel title="年齡長條圖" eyebrow="AGE HISTOGRAM" aside={<span className="sample">已知 N = {formatNumber(knownAges.length)}</span>}>
          {knownAges.length ? <Chart option={ageHistogram} label="達標者年齡直方圖" /> : <NoData />}
        </Panel>
        <Panel title="年齡層人數" eyebrow="AGE GROUPS" aside={<span className="sample">含未知 N = {formatNumber(runners.length)}</span>}>
          {runners.length ? <Chart option={ageGroupOption} label="達標者年齡層人數長條圖" /> : <NoData />}
        </Panel>
      </div>

      <Panel title="年齡層 × 性別" eyebrow="STACKED GROUPS" aside={<span className="sample">N = {formatNumber(runners.length)}</span>}>
          {runners.length ? <Chart option={ageStack} label="年齡層與性別堆疊長條圖" /> : <NoData />}
      </Panel>

      <Panel title="各性別活動中位數" eyebrow="MEDIAN BY GENDER" aside={<span className="sample"></span>}>
        {/* 達標者組成，不是達標率 */}
        <div className="data-cards">
          {medians.map((row, index) => (
            <article key={row.label} style={{ borderTopColor: CHART_COLORS[index] }}>
              <h3>{row.label}</h3>
              <dl>
                <div><dt>累積活動時間</dt><dd>{row.time == null ? "—" : formatDuration(row.time)}</dd></div>
                <div><dt>跑步次數</dt><dd>{row.activities == null ? "—" : `${row.activities.toLocaleString()} 次`}</dd></div>
              </dl>
            </article>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function NoData() {
  return <div className="empty-state"><span>×</span><p>目前篩選條件沒有可分析的資料。</p></div>;
}
